import { Link, type To } from 'react-router'
import { CaretRightIcon, Mtds } from 'prove-common'
import type { DelegationLevelItem } from './useDelegationLevel'
import { DelegationCardProgress } from './DelegationCardProgress'
import { formatUnitCount } from '../../utils/delegationExplorerPath'
import { QuantityStepper } from '../quantity-stepper/QuantityStepper'

type DelegationBranchCardProps = {
  item: DelegationLevelItem
  typeLabel: string
  to: To
  canEdit: boolean
  isMutating: boolean
  onChange: (quantity: number) => void
}

/**
 * Kort for et nivå som kan åpnes videre, for eksempel en avdeling eller seksjon.
 * Eldre fordelinger som stopper her kan fortsatt endres.
 */
export const DelegationBranchCard = ({
  item,
  typeLabel,
  to,
  canEdit,
  isMutating,
  onChange
}: Readonly<DelegationBranchCardProps>) => {
  const linkId = `delegation-branch-${item.ownerId.type}-${item.ownerId.owner}`
  const content = (
    <Mtds.Grid data-gap={3} className='h-full grid-rows-[auto_1fr_auto]'>
      <Mtds.Grid data-gap={0}>
        <Mtds.Flex data-justify='space-between' data-align='center' data-nowrap>
          <Mtds.Muted>{typeLabel}</Mtds.Muted>
          {item.isLegacyTarget ? (
            <Link id={linkId} to={to} aria-label={`Vis ${item.name}`}>
              <CaretRightIcon aria-hidden />
            </Link>
          ) : (
            <CaretRightIcon aria-hidden />
          )}
        </Mtds.Flex>
        <Mtds.Heading as='h3' data-size='xs'>
          {item.name}
        </Mtds.Heading>
        <Mtds.Muted>
          {[
            ...item.childCounts.map(({ type, count }) =>
              formatUnitCount(count, type)
            ),
            `${item.plannedQuantity} planlagt`
          ].join(' · ')}
        </Mtds.Muted>
      </Mtds.Grid>
      {item.isLegacyTarget ? (
        <Mtds.Grid data-gap={2} data-align-content='end'>
          <QuantityStepper
            label={`Prøver til ${item.name}`}
            value={item.plannedQuantity}
            min={0}
            disabled={!canEdit || isMutating}
            onChange={onChange}
          />
        </Mtds.Grid>
      ) : (
        <span />
      )}
      <DelegationCardProgress
        samplesTaken={item.samplesTaken}
        quantity={item.plannedQuantity}
      />
    </Mtds.Grid>
  )

  return item.isLegacyTarget ? (
    <Mtds.Card className='h-full min-h-72' data-clickdelegatefor={linkId}>
      {content}
    </Mtds.Card>
  ) : (
    <Mtds.Card as={Link} to={to} className='h-full min-h-72'>
      {content}
    </Mtds.Card>
  )
}
